import { Inject, Injectable, Optional, inject } from '@angular/core';
import {
  NavbarConfigService,
  AuthService,
  GungFlowService,
  AssortmentService,
  SelectedCustomerService,
  NavGroup,
  NavLink,
  GungModalService,
  CartService,
  CustomerService,
  NewsActionConfigService,
  NavAction,
  BackendInterceptor,
  DocumentsService,
  PriceConfigService,
  Customer,
  BaseViewConfigService,
  GungCompanyService,
  NavigationConfig,
  LocationConfigService,
  MetadataService,
  SelectedSupplierService,
  GungFeatureMap,
  GungNavbarService
} from 'gung-standard';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { forkJoin, Observable, of, Subject, takeUntil, tap, shareReplay } from 'rxjs';
import { filter, first, mergeMap, map } from 'rxjs';
import { Location } from '@angular/common';
import { OttoOlsenPimEditConfigService } from './otto-olsen-pim-edit-config.service';
import { OttoOlsenRoleSwitcherHelperService } from './otto-olsen-role-switcher-helper.service';
import { BackendFeatureService } from 'gung-standard';
import { HttpClient } from '@angular/common/http';
import { fnProductName } from '../pipes/product-name.pipe';
import { GungUser as User } from 'gung-standard';

@Injectable({
  providedIn: 'root'
})
export class OttoOlsenNavbarConfigService extends NavbarConfigService {
  enableCreateProduct = true;
  enableAccountRequestManage = true;
  isEnabledShowUserNewsInPage = false;
  isViewOnly = false;
  isScannerOnly = false;
  enableQuickorderScanner = true;
  enableBarcodeScanner = true;
  isAdmin = false;
  isPunchout = false;
  unsubscribe: Subject<boolean> = new Subject<boolean>();

  user: User;

  // getMainMenu() re-runs on every selectedCustomer emission (of which there can be several during
  // login/bootstrap), so this fetch is cached to avoid re-hitting /json/customers on every re-run.
  private customers$?: Observable<Customer[]>;

  private ottoOlsenPimEditConfigService = inject(OttoOlsenPimEditConfigService);
  private ottoOlsenRoleSwitcherHelperService = inject(OttoOlsenRoleSwitcherHelperService);

  constructor(
    @Optional()
    @Inject('environment')
    protected environment: NavigationConfig
  ) {
    super(environment);
    this.authService.getHasRoles('VIEW').subscribe(isViewOnly => (this.isViewOnly = isViewOnly));
    this.authService
      .getHasRoles('SCANNER')
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(isScannerOnly => (this.isScannerOnly = isScannerOnly));
    this.authService.getHasRoles('ADMIN').subscribe(admin => (this.isAdmin = admin));
    this.authService
      .getHasRoles('PUNCHOUT')
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(isPunchout => (this.isPunchout = isPunchout));
    this.authService
      .getCurrentUser()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(user => (this.user = user));
  }

  private getCachedCustomers(): Observable<Customer[]> {
    if (!this.customers$) {
      this.customers$ = this.customerService.getCustomers().pipe(shareReplay(1));
    }
    return this.customers$;
  }

  public getMainMenu(): Observable<NavGroup> {
    return super.getMainMenu().pipe(
      mergeMap(menu =>
        forkJoin([
          of(menu),
          this.getCachedCustomers().pipe(first()),
          this.authService.getRoles().pipe(first())
        ])
      ),
      map(([menu, customers, userRoles]) => {
        const idxDeliveryLocation = (menu.links[0] as NavGroup).links.findIndex(m => m.heading === 'DELIVERY_LOCATION');
        if (idxDeliveryLocation > -1) {
          if (customers.length < 2) {
            (menu.links[0] as NavGroup).links.splice(idxDeliveryLocation, 1);
          } else {
            (menu.links[0] as NavGroup).links[idxDeliveryLocation].heading = 'DELIVERY_LOCATION_MENU';
          }
        }
        /* const logout = (menu.links[1] as NavGroup).(m => m.heading === 'LOGOUT'); */
        if (menu.links[1].heading === 'LOGOUT') {
          menu.links[1].icon = 'fa-arrow-right-from-bracket';
        }
        if (this.ottoOlsenPimEditConfigService.hasPimEditUrlsRoles(userRoles)) {
          const idxHidePrice = menu.links.findIndex(m => m.heading === 'HIDE_PRICE');
          const showEditPim = {
            link: undefined,
            heading: 'HIDE_EDIT_PIM_LINKS',
            action: (inputElement: HTMLInputElement) => {
              this.ottoOlsenPimEditConfigService.pimEditUrlHide.next(inputElement.checked);
            }
          };
          menu.links.splice(idxHidePrice > -1 ? idxHidePrice : 0, 0, showEditPim);
        }

        if (this.isPunchout && menu.links) {
          // Punchout users must not see HIDE_PRICE or the ACTUATOR mock-role toggles
          menu.links = menu.links.filter(
            m =>
              m.heading !== 'HIDE_PRICE' &&
              (m as NavLink).link !== 'TOGGLE' &&
              m.heading !== 'Enable mock roles' &&
              m.heading !== 'Disable mock roles'
          );
        }

        return menu;
      })
    );
  }

  public getLeftMenus(): Observable<NavGroup[]> {
    const baseMenu: NavGroup[] = [
      // {
      //   heading: 'QUICK_SEARCH',
      //   link: '/quick-products'
      // },
    ];

    return this.authService.getCurrentUser().pipe(
      filter(user => !!user),
      first(),
      mergeMap(user => {
        return forkJoin([
          this.assortmentService.getAssortmentRecursiveNoProducts(user.assortment).pipe(first()),
          user.roles.includes('ANONYMOUS')
            ? of(null)
            : this.selectedCustomerService.getSelectedCustomer().pipe(
              filter(customer => !!customer),
              first()
            )
        ]);
      }),
      map(([assortment, customer]) => {
        const productsMenu = {
          heading: 'PRODUCTS',
          link: this.getProductListBaseRoute(),
          links: undefined
        } as NavGroup;
        if (assortment && assortment.children) {
          productsMenu.links = assortment.children.map(
            childAssortment =>
              ({
                heading: fnProductName(childAssortment),
                link: `/articles/${childAssortment.id}`,
                links: childAssortment.children.map(
                  subChildAssortment =>
                    ({
                      heading: fnProductName(subChildAssortment),
                      link: `/articles/${childAssortment.id}/${subChildAssortment.id}`
                    }) as NavLink
                )
              }) as NavGroup
          );
        }
        baseMenu.unshift(productsMenu);
        if (customer) {
          baseMenu.push({
            link: '/favourites',
            heading: 'FAVOURITES'
          });
        }
        return baseMenu;
      })
    );
  }

  protected getAdminMenu(): (NavLink | NavAction | NavGroup)[] {
    const menuLinks = super.getAdminMenu();

    (menuLinks[0] as NavGroup).links.push();

    const systemGroupIndex = menuLinks.findIndex(group => (group as NavGroup).heading === 'SYSTEM');
    if (systemGroupIndex > -1) {
      const usersIndex = (menuLinks[systemGroupIndex] as NavGroup).links.findIndex(m => m.heading === 'USERS');
      (menuLinks[systemGroupIndex] as NavGroup).links.splice(usersIndex > -1 ? usersIndex + 1 : 0, 0, {
        link: '/admin/cross-references',
        heading: 'CROSS_REFERENCES'
      });

      const crossReferencesIndex = (menuLinks[systemGroupIndex] as NavGroup).links.findIndex(
        m => m.heading === 'CROSS_REFERENCES'
      );
      (menuLinks[systemGroupIndex] as NavGroup).links.splice(
        crossReferencesIndex > -1 ? crossReferencesIndex + 1 : 0,
        0,
        {
          link: '/admin/cross-reference-stats',
          heading: 'FILTER_SEARCH_STATS'
        }
      );
    }

    menuLinks.unshift({
      heading: '',
      links: [
        {
          heading: 'PDF_TO_CART',
          link: '/pdf-to-cart'
        }
      ]
    });
    if (
      (this.user?.extra?.viewBlockOrders || this.user?.extra?.createBlockOrders) &&
      (
        this.roles.includes('ADMIN') ||
        this.roles.includes('SALES') ||
        this.roles.includes('ASSORTMENT-MANAGER') ||
        this.roles.includes('PRODUCT-MANAGER')
      )
    ) {
      (menuLinks[0] as NavGroup).links.unshift({
        link: '/block-order-agreements',
        heading: 'BLOCK_ORDER_AGREEMENTS',
        icon: 'fa-boxes-packing'
      });
    }

    // Add BETA QUICKORDER SCANNER
    if (this.enableQuickorderScanner) {
      (menuLinks[0] as NavGroup).links.push({
        link: '/barcode-scanner',
        heading: 'BARCODE_SCANNER',
        icon: 'fa-barcode'
      });
      // (menuLinks[0] as NavGroup).links.splice((menuLinks[0] as NavGroup).links.length, 0, {
      //   link: '/quickorder-scanner',
      //   heading: 'BARCODE_SCANNER_QUICKORDER'
      // });
    }

    (menuLinks[0] as NavGroup).links.push({
      link: '/search-cross-reference',
      heading: 'FILTER_SEARCH_CROSS_REFERENCE',
      icon: 'fa-search'
    });

    return menuLinks;
  }

  protected getUserMenu(customer: Customer): (NavLink | NavAction | NavGroup)[] {
    const menuLinks = this.getOttoUserMenu(customer) as NavGroup[];

    let previousOrdersIndex = (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'PREVIOUS_ORDERS');

    if (this.isAdmin) {
      // Add digital assets to the project
      (menuLinks[0] as NavGroup).links.splice(++previousOrdersIndex, 0, {
        link: '/products-export',
        heading: 'PRODUCT_EXPORT_NAV_BAR'
      });
    }

    if (!!customer?.extra?._customerSpecAssortment) {
      menuLinks[0].links.push({
        heading: 'EXTRA_PRODUCTS',
        link: `/products/a/${customer.extra._customerSpecAssortment}`,
        icon: 'fa-drum-steelpan'
      });
    }

    if (!this.isAdmin) {
      (menuLinks[0] as NavGroup).links.push({
        link: '/search-cross-reference',
        heading: 'FILTER_SEARCH_CROSS_REFERENCE',
        icon: 'fa-search'
      });
    }

    menuLinks[0].links.push({
      link: '/customers-dashboard',
      heading: 'CUSTOMER_DASHBOARD',
      icon: 'fa-objects-column'
    });
    if ((menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'PRODUCT_EXPORT_NAV_BAR') > -1) {
      menuLinks[0].links.splice(
        (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'PRODUCT_EXPORT_NAV_BAR'),
        1
      );
    }
    // if ((menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'SHOP_BY_PRODUCT_LIST') > -1) {
    //   menuLinks[0].links.splice((menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'SHOP_BY_PRODUCT_LIST'), 1);
    // }

    if (this.isViewOnly && (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'SAVED_CARTS') > -1) {
      menuLinks[0].links.splice(
        (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'SAVED_CARTS'),
        1
      );
    }

    if (this.isScannerOnly) {
      const barcodeScannerItem = {
        link: '/barcode-scanner',
        heading: 'BARCODE_SCANNER',
        icon: 'fa-barcode',
        action: () => {
          this.ottoOlsenRoleSwitcherHelperService.setRole('SCANNER');
        }
      };

      (menuLinks[0] as NavGroup).links.push(barcodeScannerItem);
    }

    // getSalesMenu only contributes the link when SALES also has ASSORTMENT-/PRODUCT-MANAGER, so a plain SALES user must get it here
    const salesMenuAlreadyHasBlockOrderLink =
      (this.user?.extra?.viewBlockOrders || this.user?.extra?.createBlockOrders) &&
      this.roles.includes('SALES') &&
      (this.roles.includes('ASSORTMENT-MANAGER') || this.roles.includes('PRODUCT-MANAGER'));

    // ADMIN always gets the link from getAdminMenu
    if (
      this.user?.extra?.showAllBlockOrders &&
      !this.isAdmin &&
      !salesMenuAlreadyHasBlockOrderLink &&
      // ASSORTMENT-MANAGER/PRODUCT-MANAGER may already have it from getOttoUserMenu, which fills this same array
      (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'BLOCK_ORDER_AGREEMENTS') === -1
    ) {
      (menuLinks[0] as NavGroup).links?.push({
        link: '/block-order-agreements',
        heading: 'BLOCK_ORDER_AGREEMENTS',
        icon: 'fa-boxes-packing'
      });
    }

    if (this.isPunchout) {
      const punchoutHiddenHeadings = [
        'MY_PAGES',
        'PREVIOUS_ORDERS',
        'SAVED_CARTS',
        'FAVOURITES',
        'IMPORT_LINESHEET',
        'FILTER_SEARCH_CROSS_REFERENCE',
        'CUSTOMER_DASHBOARD'
      ];
      (menuLinks[0] as NavGroup).links = ((menuLinks[0] as NavGroup).links || []).filter(
        m => !punchoutHiddenHeadings.includes(m.heading)
      );
    }

    return menuLinks;
  }

  protected getOttoUserMenu(
    customer: Customer,
    isSales?: boolean,
    customers?: Customer[],
    isActuator?: boolean,
    features?: GungFeatureMap
  ): (NavLink | NavAction | NavGroup)[] {
    const menuLinks: NavGroup[] = [
      {
        heading: '',
        links: [
          {
            link: `${'/customers/' + customer.id}`,
            heading: 'MY_PAGES',
            icon: 'fa-address-card'
          },
          {
            link: '/delivery-location',
            heading: 'DELIVERY_LOCATION',
            icon: 'fa-location-dot'
          },
          {
            link: '/orders',
            heading: 'PREVIOUS_ORDERS',
            icon: 'fa-memo'
          },
          {
            link: '/products-export',
            heading: 'PRODUCT_EXPORT_NAV_BAR',
            icon: 'fa-file-export'
          },
          {
            link: '/carts',
            heading: 'SAVED_CARTS',
            icon: 'fa-cart-shopping'
          },
          {
            link: '/saved-quotes',
            heading: 'SAVED_QUOTES'
          },
          {
            link: '/favourites',
            heading: 'FAVOURITES',
            icon: 'fa-star'
          },
          {
            link: '/admin/import-linesheet',
            heading: 'IMPORT_LINESHEET',
            icon: 'fa-file-excel'
          }
        ]
      }
    ];

    if (!isSales) {
      // Remove SAVED_QUOTES if not SALES
      menuLinks[0].links.splice(
        (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'SAVED_QUOTES'),
        1
      );
    } else {
      menuLinks[0].links.splice(
        (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'MY_PAGES'),
        1
      );
      menuLinks[0].links.splice(
        (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'DELIVERY_LOCATION'),
        1
      );
    }

    if (
      customers &&
      customers.length < 2 &&
      (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'DELIVERY_LOCATION') > -1
    ) {
      // Remove DELIVERY_LOCATION if only one delivery location
      menuLinks[0].links.splice(
        (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'DELIVERY_LOCATION'),
        1
      );
    }

    let favouritesIndex = (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'FAVOURITES');
    if (this.enableDigitalAssets) {
      // Add digital assets to the project
      (menuLinks[0] as NavGroup).links.splice(++favouritesIndex, 0, {
        link: '/digital-assets',
        heading: 'DIGITAL_ASSETS'
      });
    }

    if (this.enableActivityInternal) {
      // Add activity internal to the project
      (menuLinks[0] as NavGroup).links.splice(++favouritesIndex, 0, {
        link: '/activity-internal',
        heading: 'ACTIVITIES'
      });
    }

    if (this.isEnabledShowUserNewsInPage) {
      (menuLinks[0] as NavGroup).links.push({ link: '/news', heading: 'NEWS' });
    }

    if (this.enableWhiteboardManagement) {
      (menuLinks[0] as NavGroup).links.push({ link: '/whiteboard-management', heading: 'WHITEBOARD' });
    }

    if (this.enableExportPdf) {
      (menuLinks[0] as NavGroup).links.push({
        link: '/export-pdf',
        heading: 'WHITEBOARD',
        action: () => this.router.navigate(['export-pdf'], { queryParams: { filters: 'ARCHIVED__:__NO' } })
      });
    }

    if (this.enableDocumentUserGroups) {
      this.documents.forEach(doc => {
        const url = this.backendInterceptor.getBaseUrl() + '/gung-document-archive/document/' + doc.id;
        menuLinks[0].links.push({
          heading:
            doc.metaData.description && doc.metaData.description !== 'undefined'
              ? doc.metaData.description
              : doc.filename,
          action: () => {
            window.open(url, '_blank');
          }
        });
      });
    }

    if (
      this.hasFeatureWithVersion(features, 'reportCentral', undefined) &&
      this.featureVisibleForRole(features, 'reportCentral', 'USER')
    ) {
      menuLinks[0].links.push({
        link: '/report-central',
        heading: 'REPORT_CENTRAL'
      });
    }

    if (
      (this.user?.extra?.viewBlockOrders || this.user?.extra?.createBlockOrders) &&
      !this.roles.includes('ADMIN') &&
      !this.roles.includes('SALES') &&
      (
        this.roles.includes('ASSORTMENT-MANAGER') ||
        this.roles.includes('PRODUCT-MANAGER')
      )
    ) {
      menuLinks[0].links.push({
        link: '/block-order-agreements',
        heading: 'BLOCK_ORDER_AGREEMENTS'
      });
    }

    return menuLinks;
  }

  protected getSalesMenu(
    isActuator?: boolean,
    roles?: string[],
    salesCodeErp?: string
  ): (NavLink | NavAction | NavGroup)[] {
    const menuLinks: NavGroup[] = super.getSalesMenu(isActuator, roles, salesCodeErp);
    const idx = (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'QUICK_ORDER');
    menuLinks[0].links.splice(idx > -1 ? idx : 0, 0, { link: '/quick-search', heading: 'QUICK_SEARCH' });
    (menuLinks[0] as NavGroup).links.splice((menuLinks[0] as NavGroup).links.length, 0, {
      link: '/admin/create-product',
      heading: 'CREATE_PRODUCT'
    });
    const idy = (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'SALES_DASHBOARD');
    menuLinks[0].links.splice(idy > -1 ? idy : 0, 0, {
      link: '/sales-dashboard-beta',
      heading: 'SALES_DASHBOARD_BETA'
    });

    if (roles.indexOf('ADMIN') === -1) {
      menuLinks[0].links.push({
        link: '/sales/manage-users',
        heading: 'USERS'
      });
    }

    const idz = (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'UPLOAD_EXCEL');
    if (idz > -1) {
      menuLinks[0].links.splice(idz, 1);
    }

    const importIndex = (menuLinks[0] as NavGroup).links.findIndex(m => m.heading === 'IMPORT_LINESHEET');
    if (importIndex > -1) {
      menuLinks[0].links.splice(importIndex, 1);
    }

    menuLinks[0].links.push({
      link: '/delivery-overview',
      heading: 'DELIVERY_OVERVIEW',
      icon: ''
    });

    menuLinks[0].links.push({
      link: '/sales/edi',
      heading: 'EDI'
    });

    if (
      (this.user?.extra?.viewBlockOrders || this.user?.extra?.createBlockOrders) &&
      !this.roles.includes('ADMIN') &&
      (
        this.roles.includes('SALES') &&
        this.roles.includes('ASSORTMENT-MANAGER') ||
        this.roles.includes('PRODUCT-MANAGER')
      )
    ) {
      menuLinks[0].links.push({
        link: '/block-order-agreements',
        heading: 'BLOCK_ORDER_AGREEMENTS'
      });
    }

    return menuLinks;
  }

  public getTopInformation(): Observable<NavLink> {
    return this.userRoles().pipe(
      first(),
      mergeMap(roles =>
        forkJoin([
          this.selectedCustomerService.getSelectedCustomer().pipe(
            filter(customer => !!customer),
            first()
          ),
          of(roles.indexOf('ANONYMOUS') > -1),
          this.authService.getCurrentUser().pipe(first())
        ])
      ),
      map(([customer, isAnonymous, user]) => {
        if (this.isScannerOnly) {
          return {
            heading: customer.name + ' ' + customer.id + '<br>' + user.email,
            link: `/customers/${customer.id}`
          } as NavLink;
        }
        if (isAnonymous) {
          return null;
        }
        return {
          heading: customer.name + ' ' + customer.id,
          link: `/customers/${customer.id}`
        } as NavLink;
      })
    );
  }

  priceMenu(): Observable<NavGroup> {
    return this.userRoles().pipe(
      map(roles => {
        if (!roles) {
          return null;
        } else {
          if (roles.indexOf('SALES') > -1 || roles.indexOf('ADMIN') > -1) {
            if (this.priceConfigService.enableHidePrice) {
              return {
                link: undefined,
                heading: 'HIDE_PRICE',
                action: (inputElement: HTMLInputElement) => {
                  this.priceConfigService.setHidePrice(inputElement.checked);
                }
              };
            }
          }
        }
      })
    );
  }
}
